import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Stack,
  Button,
  Card,
  CardContent,
  Tooltip,
  Chip,
  ListItemAvatar,
  Avatar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import { Client } from "@microsoft/microsoft-graph-client";
import { AccountInfo, InteractionRequiredAuthError, IPublicClientApplication } from "@azure/msal-browser";
import UserPicker, { GraphUser } from "./UserPicker";
import GroupIcon from '@mui/icons-material/Group';

// Simplified Owner interface matching the usage in AppTester
export interface Owner {
  id: string;
  displayName?: string;
  userPrincipalName?: string;
}

// ResolvedOwner includes a friendlyName for display
export interface ResolvedOwner extends Owner {
  friendlyName: string;
}

interface OwnersProps {
  appId: string;                        // e.g. pass in app.id from the parent
  instance: IPublicClientApplication;   // MSAL instance from parent
  accounts: AccountInfo[];             // the signed-in user accounts from parent
  onApplyChanges: () => void;          // callback for "Apply Changes" if needed
}

const Owners: React.FC<OwnersProps> = ({
  appId,
  instance,
  accounts,
  onApplyChanges,
}) => {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [resolvedOwners, setResolvedOwners] = useState<ResolvedOwner[]>([]);
  const [newOwnerId, setNewOwnerId] = useState("");
  
  // Add state for alerts
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [showRemoveReminder, setShowRemoveReminder] = useState(false);
  const [ownerActionName, setOwnerActionName] = useState("");

  // State for User Picker
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [graphTokenForUserSearch, setGraphTokenForUserSearch] = useState("");

  // 1) Fetch the /owners for this application
  useEffect(() => {
    if (!appId) return;

    const fetchOwners = async () => {
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["Application.ReadWrite.All"],
          account: accounts[0],
        });
        const client = Client.init({
          authProvider: (done) => done(null, response.accessToken),
        });

        const ownersData = await client.api(`/applications/${appId}/owners`).get();
        setOwners(ownersData.value || []);
      } catch (error) {
        console.error("Failed to fetch owners:", error);
      }
    };

    fetchOwners();
  }, [appId, instance, accounts]);

  // 2) Resolve friendlyName for each owner
  useEffect(() => {
    const resolveOwnerNames = async () => {
      if (owners.length === 0) {
        setResolvedOwners([]);
        return;
      }
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["Application.ReadWrite.All"],
          account: accounts[0],
        });
        const client = Client.init({
          authProvider: (done) => done(null, response.accessToken),
        });

        const resolved: ResolvedOwner[] = [];
        for (const owner of owners) {
          try {
            const ownerData = await client.api(`/directoryObjects/${owner.id}`).get();
            const friendlyName =
              ownerData.displayName ||
              ownerData.userPrincipalName ||
              ownerData.appDisplayName ||
              owner.id;

            resolved.push({ 
              ...owner, 
              friendlyName,
              userPrincipalName: ownerData.userPrincipalName || null,
              displayName: ownerData.displayName || null
            });
          } catch (e) {
            console.error("Error resolving owner name:", e);
            resolved.push({ ...owner, friendlyName: owner.id });
          }
        }
        setResolvedOwners(resolved);
      } catch (error) {
        console.error("Error resolving owner names:", error);
      }
    };

    resolveOwnerNames();
  }, [owners, instance, accounts]);

    // Helper to get Graph Access Token for User Picker
  async function getGraphAccessTokenForUserPicker(): Promise<string> {
    const accessTokenRequest = {
      scopes: ["User.Read.All"], // Scope for reading user profiles
      account: accounts[0],
    };
    try {
      const response = await instance.acquireTokenSilent(accessTokenRequest);
      return response.accessToken;
    } catch (err: any) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup(accessTokenRequest);
        return response.accessToken;
      }
      throw err;
    }
  }

  // Opens the user picker modal
  async function handleOpenUserPicker() {
    try {
      const token = await getGraphAccessTokenForUserPicker();
      setGraphTokenForUserSearch(token);
      setUserPickerOpen(true);
    } catch (err) {
      console.error("Failed to get Graph token for user search:", err);
      alert("Failed to get Graph token for user picker. See console for details.");
    }
  }

  // Receives the user(s) picked from the directory and sets the UPN
  function handleUserPickerSubmit(selectedUsers: GraphUser[]) {
    setUserPickerOpen(false);
    if (selectedUsers.length > 0 && selectedUsers[0].userPrincipalName) {
      setNewOwnerId(selectedUsers[0].userPrincipalName);
    }
  }

  // 3) Add Owner by UPN
  const handleAddOwner = async () => {
    if (!newOwnerId) return; // user typed nothing
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0],
      });
      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken),
      });

      // 1) Look up user by UPN
      const userData = await client.api(`/users/${newOwnerId}`).get();
      if (!userData?.id) {
        alert(`User with UPN '${newOwnerId}' not found`);
        return;
      }

      // 2) POST to /applications/{appId}/owners/$ref
      await client.api(`/applications/${appId}/owners/$ref`).post({
        "@odata.id": `https://graph.microsoft.com/v1.0/directoryObjects/${userData.id}`,
      });

      // 3) Refresh
      const ownersData = await client.api(`/applications/${appId}/owners`).get();
      setOwners(ownersData.value || []);

      // 4) Clear field
      setNewOwnerId("");
      
      // 5) Show reminder instead of alert
      setOwnerActionName(userData.displayName || userData.userPrincipalName || "New owner");
      setShowAddReminder(true);
      
      // Auto-hide after 6 seconds
      setTimeout(() => {
        setShowAddReminder(false);
      }, 6000);
    } catch (error) {
      console.error("Error adding owner:", error);
      alert("Failed to add owner. Check console.");
    }
  };

  // 4) Remove Owner
  const handleRemoveOwner = async (ownerId: string) => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0],
      });
      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken),
      });

      // Find the owner before removal to get their name
      const ownerToRemove = resolvedOwners.find(owner => owner.id === ownerId);
      const ownerName = ownerToRemove?.friendlyName || ownerId;

      await client.api(`/applications/${appId}/owners/${ownerId}/$ref`).delete();
      const ownersData = await client.api(`/applications/${appId}/owners`).get();
      setOwners(ownersData.value || []);

      // Show reminder instead of alert
      setOwnerActionName(ownerName);
      setShowRemoveReminder(true);
      
      // Auto-hide after 6 seconds
      setTimeout(() => {
        setShowRemoveReminder(false);
      }, 6000);
    } catch (error) {
      console.error("Error removing owner:", error);
      alert("Failed to remove owner. Check console.");
    }
  };

  return (
  <Card variant="outlined" sx={{ mb: 4 }}>
    <CardContent>
      {/* Main Title for the entire Owners section */}
      {/* <Typography 
        variant="h6" 
        sx={{ 
          fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
          fontSize: "1.3rem",
          fontWeight: 600,
          color: "primary.main",
          letterSpacing: "0.01em",
          mb: 3
        }}
      >
        Application Owners
      </Typography> */}

      {/* 1) PROVISIONED OWNERS - Updated with green theme like OptionalClaims */}
      <Accordion 
        defaultExpanded
        sx={{
          border: '1px solid #e8f5e9', // Green border like OptionalClaims
          borderRadius: '12px !important', // Rounded corners like OptionalClaims
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)', // Same shadow as OptionalClaims
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)', // Green gradient like OptionalClaims
            borderBottom: '1px solid #c8e6c9',
            minHeight: '56px' // Same height as OptionalClaims
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28, // Same size as OptionalClaims
              height: 28,
              borderRadius: '8px', // Rounded square like OptionalClaims
              backgroundColor: '#4caf50', // Green background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <SecurityIcon sx={{ color: 'white', fontSize: '0.85rem' }} /> {/* Use SecurityIcon for owners */}
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.2rem", // Same as OptionalClaims
                fontWeight: 600,
                color: "#2e7d32", // Green text color
                letterSpacing: "0.01em"
              }}
            >
              Provisioned Owners
            </Typography>
            {/* Add count badge like OptionalClaims */}
            <Chip 
              size="small"
              label={`${resolvedOwners.length} owners`}
              sx={{ 
                ml: 2,
                backgroundColor: '#c8e6c9', // Light green background
                color: '#2e7d32', // Dark green text
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ 
          backgroundColor: "#fafffe", // Light green background like OptionalClaims
          p: 3, 
          borderRadius: 1 
        }}>
          {/* Remove Reminder Alert */}
          {showRemoveReminder && (
            <Box sx={{ 
              p: 1.5, 
              mb: 2,
              backgroundColor: '#fdf5f5', 
              borderLeft: '4px solid #f44336',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <Box sx={{ mr: 1.5, color: '#d32f2f' }}>
                <InfoOutlinedIcon />
              </Box>
              <Typography variant="body2">
                <strong>"{ownerActionName}" removed!</strong> Remember to click <strong>Apply Changes</strong> to finalize this removal.
              </Typography>
            </Box>
          )}
          
          {resolvedOwners.length === 0 ? (
            <Box sx={{ 
              p: 4, 
              backgroundColor: '#f8f9fa', 
              borderRadius: '12px', 
              textAlign: 'center',
              border: '2px dashed #dee2e6'
            }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: '#e9ecef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <SecurityIcon sx={{ fontSize: '2rem', color: '#6c757d' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No owners are provisioned to this application
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the "Add New Owner" section below to provision owners
              </Typography>
            </Box>
          ) : (
            <List sx={{ 
              bgcolor: "#ffffff", 
              border: "1px solid #c8e6c9", // Green border to match theme
              borderRadius: '8px', // Updated border radius
              mb: 2,
              overflow: 'hidden'
            }}>
              {resolvedOwners.map((owner, index) => (
                <ListItem
                  key={owner.id}
                  sx={{
                    borderBottom: index < resolvedOwners.length - 1 ? "1px solid #f5f5f5" : "none",
                    '&:hover': { backgroundColor: '#fafafa' } // Added hover effect
                  }}
                  secondaryAction={
                    <Tooltip title="Remove owner">
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleRemoveOwner(owner.id)}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#ffebee'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#4caf50' }}> {/* Green avatar background */}
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {owner.friendlyName}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      owner.userPrincipalName ? (
                        <Typography variant="caption" color="text.secondary">
                          {owner.userPrincipalName}
                        </Typography>
                      ) : (
                        <Chip 
                          label="Service Principal" 
                          size="small" 
                          sx={{
                            height: 20, 
                            fontSize: '0.7rem',
                            backgroundColor: '#c8e6c9', // Green chip background
                            color: '#2e7d32' // Dark green text
                          }}
                          variant="outlined"
                        />
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
          
          {/* Apply Changes Button */}
          {showRemoveReminder && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mt: 2, 
              pt: 1, 
              borderTop: '1px solid #c8e6c9' // Green border
            }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={() => {
                  onApplyChanges();
                  setShowRemoveReminder(false);
                }}
                sx={{ borderRadius: '8px' }} // Rounded button
              >
                Apply Changes
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* 2) ADD NEW OWNER - Updated with blue theme like OptionalClaims */}
      <Accordion
        sx={{
          border: '1px solid #e3f2fd', // Blue border like OptionalClaims
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          mb: 2
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%)', // Blue gradient like OptionalClaims
            borderBottom: '1px solid #bbdefb',
            minHeight: '56px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              backgroundColor: '#2196f3', // Blue background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}>
              <PersonAddIcon sx={{ color: 'white', fontSize: '1rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontSize: "1.2rem", // Same as OptionalClaims
                fontWeight: 600,
                color: "#1565c0", // Blue text color
                letterSpacing: "0.01em"
              }}
            >
              Add New Owner
            </Typography>
            <Chip 
              size="small"
              label="Configure" 
              sx={{ 
                ml: 2,
                backgroundColor: '#bbdefb', // Light blue background
                color: '#1565c0', // Dark blue text
                fontWeight: 500
              }}
            />
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ 
          backgroundColor: "#f8fbff", // Light blue background like OptionalClaims
          p: 3, 
          borderRadius: 1 
        }}>
          {/* Add Reminder Alert */}
          {showAddReminder && (
            <Box sx={{ 
              p: 1.5, 
              mb: 2,
              backgroundColor: '#fff3e0', 
              borderLeft: '4px solid #ff9800',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <Box sx={{ mr: 1.5, color: '#f57c00' }}>
                <InfoOutlinedIcon />
              </Box>
              <Typography variant="body2">
                <strong>"{ownerActionName}" added as owner!</strong> Remember to click <strong>Apply Changes</strong> to save your modifications.
              </Typography>
            </Box>
          )}

          {/* Enhanced form with better visual grouping */}
          <Stack spacing={3}>
            <Box sx={{
              p: 3,
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e3f2fd' // Blue border
            }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1565c0' }}>
                Owner Configuration
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <TextField
                  label="User Principal Name (UPN)"
                  placeholder="user@domain.com"
                  value={newOwnerId}
                  onChange={(e) => setNewOwnerId(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ 
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px'
                    }
                  }}
                  helperText="Enter UPN or click Browse to select a user"
                />
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={handleOpenUserPicker}
                  sx={{ 
                    mt: 0,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Browse
                </Button>
              </Box>
            </Box>

            {/* Action buttons with enhanced styling */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 2, 
              borderTop: '1px solid #e3f2fd' // Blue border
            }}>
              <Button 
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={handleAddOwner}
                disabled={!newOwnerId}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Add Owner
              </Button>
              
              <Button 
                variant="contained" 
                color="success" 
                startIcon={<SaveIcon />}
                onClick={() => {
                  onApplyChanges();
                  setShowAddReminder(false);
                }}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Apply Changes
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

        {/* 3) ABOUT OWNERS */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography 
              variant="subtitle1"
              sx={{ 
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <InfoOutlinedIcon sx={{ mr: 1, fontSize: "1.1rem", color: 'info.main' }} />
              About Application Owners
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ p: 2, backgroundColor: "#f5f5ff", borderRadius: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Application owners are users who have administrative access to manage an application registration in Azure Active Directory.
              </Typography>
              
              {/* Visual explanation */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#ffffff', 
                border: '1px solid #e0e0ff', 
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 1 }}>
                  What Owners Can Do:
                </Typography>
                <Typography variant="body2">
                  • <strong>Update application attributes</strong>: Modify name, reply URLs, credentials, etc.<br />
                  • <strong>Manage permissions</strong>: Add or remove API permissions<br />
                  • <strong>Configure authentication</strong>: Control sign-in settings and token configuration<br />
                  • <strong>Add other owners</strong>: Share management responsibilities with other users
                </Typography>
              </Box>
              
              <Box sx={{
                p: 2,
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: '#bf360c', mb: 1 }}>
                  Important Considerations:
                </Typography>
                <Typography variant="body2">
                  • Every application should have at least one owner<br />
                  • Only add owners who need administrative access to the application<br />
                  • Owners are not automatically granted access to use the application<br />
                  • Users must exist in your Entra ID tenant before they can be added as owners
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>

      {/* User Picker Dialog */}
      <UserPicker
        open={userPickerOpen}
        onClose={() => setUserPickerOpen(false)}
        onSubmit={handleUserPickerSubmit}
        graphAccessToken={graphTokenForUserSearch}
        multiSelect={false} // Assuming you want to select one owner at a time
      />
    </Card>
  );
};

export default Owners;